import { GroupResult } from "../../../types";
import { useEffect, useRef } from "react";
import { animated, useSpring } from "react-spring";
import styled from "styled-components";

const SlideCardContainer = styled.div`
    height: fit-content;
    min-height: 190px;
    width: 100%;
`;

const SlideCard = styled(animated.div)`
    background-color: #00000055;
    padding: 20px;
    color: white;
    border-radius: 20px;
    display: inline-block;
    font-weight: bold;
    font-size: 24px;
    box-sizing: border-box;
    width: calc(100%);
    vertical-align: top;
    min-height: 190px;
    border: 10px solid transparent;
    position: relative;

    h1 {
        margin: 0;
        font-size: 24px;
    }

    p {
        font-size: 16px;
        color: white;
        margin-top: 15px;
        margin-bottom: 0;

        &.auxiliary {
            font-size: 16px;
        }

        @media only screen and (min-width: 551px) {
            font-size: 20px;
        }
    }

    .header-icon-row {
        font-size: 56px;

        .icon {
            margin-right: 15px;

            &:last-child {
                margin-right: 0;
            }
        }

        @media only screen and (max-width: 450px) {
            font-size: 36px;
        }

        @media only screen and (max-width: 340px) {
            font-size: 24px;
        }
    }

    .overlay {
        display: block;
        position: absolute;
        background: #666d;
        width: calc(100% + 6px);    
        height: calc(100% + 6px);
        top: -3px;
        left: -3px;
        padding: 20px;
        border-radius: 20px;
        backdrop-filter: blur(10px);
        animation: overlayColorCycle 2.68s infinite alternate;
    }

    @keyframes overlayColorCycle {
        0%   { background-color: #e6393999; }
        50%  { background-color: #c36eff99; }
        100% { background-color: #1ed93199; }
    }
`;

// '#e63939', '#ff4d40', '#c36eff', '#538cff', '#53b7ff', '#1ed931'
interface ResultSlideProps {
    slideClicked: (slideNumber: number) => void;
    groupResult?: GroupResult;
    currentSlide: number;
    index: number;
    children: React.ReactNode;
    loadingResults: boolean;
}

interface LineData {
    visible: boolean;
    color: string;
    rating: number;
}

const ResultSlide: React.FC<ResultSlideProps> = (props) => {

    let lineData: LineData | null = null;

    if (props.groupResult) {
        lineData = {
            visible: props.index === props.currentSlide,
            color: props.groupResult.color ?? '#000',
            rating: props.groupResult.rating ?? 50
        };
    }

    const lineDataRef = useRef(lineData);
    lineDataRef.current = lineData;

    const currentIndex = useRef(0);
    const reversedRef = useRef(false);

    const getNextColor = () => {
        //const colorArray: string[] = ['#e63939', '#ff4d40', '#c36eff', '#538cff', '#53b7ff', '#1ed931'];
        const colorArray: string[] = ['#e63939', '#c36eff', '#1ed931'];
        const nextColor = colorArray[currentIndex.current];

        if (reversedRef.current) {
            if (currentIndex.current === 0) {
                reversedRef.current = false;
            }
        } else {
            if (currentIndex.current === colorArray.length - 1) {
                reversedRef.current = true;
            }
        }

        if (reversedRef.current) {
            currentIndex.current--;
        } else {
            currentIndex.current++;
        }

        return nextColor;
    };

    const [resultSlideStyle, resultSlideApi] = useSpring(() => ({
        backgroundColor: `#00000055`,
        border: '3px solid transparent',
        config: {
            duration: 370,
            mass: 20,
            tension: 1,
            friction: 180,
            clamp: true
        }
    }));

    const loadingResultsRef = useRef<boolean>(props.loadingResults);

    const fadeToNextColor = () => {
        if (loadingResultsRef.current) {
            resultSlideApi.start({ 
                backgroundColor: `${getNextColor()}55`,
                onRest: fadeToNextColor
            });
        }
    };

    useEffect(() => {
        loadingResultsRef.current = props.loadingResults;

        if(props.loadingResults) {
            fadeToNextColor();
        } else {
            if (lineData) {
                resultSlideApi.stop();
                resultSlideApi.start({
                    backgroundColor: `${lineData.color}4d`,
                    border: `3px solid ${lineData.color}80`,
                    config: { duration: 670 }
                });
            } else {
                resultSlideApi.stop();
                resultSlideApi.start({
                    backgroundColor: `#00000055`,
                    border: '3px solid transparent',
                    config: { duration: 670 }
                });
            }
        }
    }, [props.loadingResults]);

    return (
        <SlideCardContainer onClick={() => props.slideClicked(1)}>
            <SlideCard style={resultSlideStyle}>
                {props.loadingResults && (
                    <div className='overlay'>
                        <p style={{ fontWeight: 'bold' }}>Изчисляваме резултатите<br/>...</p>
                    </div>
                )}
                {props.children}
            </SlideCard>
        </SlideCardContainer>
    );
};

export default ResultSlide;